<script>
import useVuelidate from '@vuelidate/core';
import {maxLength} from '@vuelidate/validators';
import DateDivider from '@views/threads/parts/DateDivider.vue';
import MessageBubble from '@views/threads/parts/MessageBubble.vue';
import ThreadMessagesHeader from '@views/threads/parts/ThreadMessagesHeader.vue';
import AkButtonActionV2 from '@components/v2/general/AkButtonActionV2.vue';
import MessageMediaMenu from '@views/threads/parts/MessageMediaMenu.vue';
import threadMixin from '@mixins/const/threadMixin';
import MessageMixin from '@mixins/messageMixin';
import threadService from '@services/threadService';
import moment from 'moment';
import store from '@store';
import AkPictureAccommodation from '@components/v2/general/AkPictureAccommodation.vue';
import AkShimmer from '@components/v2/general/AkShimmer.vue';

export default {
  name: 'ThreadMessages',
  mixins: [threadMixin, MessageMixin],
  components: {
    AkShimmer,
    AkPictureAccommodation,
    AkButtonActionV2,
    ThreadMessagesHeader,
    MessageBubble,
    DateDivider,
    MessageMediaMenu,
  },
  props: {
    modelValue: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    archivable: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:modelValue', 'message-sent', 'archive', 'openDetailsSidebar'],
  setup() {
    return {v$: useVuelidate()};
  },
  data() {
    return {
      newMessage: {
        text: '',
        media: [],
      },
      sending: false,
      isScrolled: true,
    };
  },
  validations() {
    return {
      newMessage: {
        text: {maxLengthValue: maxLength(2000)},
      },
    };
  },
  computed: {
    conversation: {
      get() {
        if (!this.modelValue) return null;
        return {...this.modelValue.conversation, guestNames: this.modelValue.conversation.guests.map((g) => g.name)};
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    lastMessage() {
      if (!this.conversation) return null;
      const messages = this.conversation.messages;
      return messages[messages.length - 1];
    },
    messagesByDay() {
      if (!this.conversation) return [];
      const groups = {};
      for (const message of this.conversation.messages) {
        const key = moment.utc(message.sentMoment).local().toDate().toDateString();
        if (!groups[key]) groups[key] = {date: moment.utc(message.sentMoment).local().toDate(), messages: []};
        groups[key].messages.push(message);
      }
      return Object.values(groups).map((group) => {
        const messages = group.messages.map((msg, i) => {
          const prev = group.messages[i - 1];
          const next = group.messages[i + 1];
          const isLastInCluster = !next || next.authorDisplay !== msg.authorDisplay || next.sent !== msg.sent;
          const isFirstInCluster = !prev || prev.authorDisplay !== msg.authorDisplay || prev.sent !== msg.sent;
          return {...msg, isLastInCluster, isFirstInCluster};
        });
        return {...group, messages};
      });
    },
    dayLabel() {
      return (date) => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
        if (date.toDateString() === yesterday.toDateString()) return 'Hier';
        return date.toLocaleDateString('fr-FR', {weekday: 'long', day: 'numeric', month: 'long'});
      };
    },
    moment() {
      return moment;
    },
    threadType() {
      return this.modelValue?.typeDetails.type;
    },
    statusLabel() {
      if (this.isReservationConfirmed) return 'Réservation confirmée';
      if (this.isSpecialOfferActive) return 'Offre envoyée';
      if (this.isSpecialOfferExpired) return 'Offre expirée';
      if (this.isSpecialOfferVoided) return 'Offre annulée';
      if (this.isReservationQuery) return 'Demande de réservation';
      if (this.isReservationDenied) return 'Demande de réservation refusée';
      if (this.isReservationRetracted) return 'Demande de réservation annulée';
      if (this.isReservationCancelled) return 'Réservation annulée';
      if (this.isReservationExpired) return 'Demande de réservation expirée';
      if (this.isInquiry) return "Demande d'informations";
      if (this.isInquiryDeclined) return "Demande d'informations refusée";
      if (this.isInquiryExpired) return "Demande d'informations expirée";
      return '';
    },
  },
  methods: {
    autoGrowMessageInput() {
      const elem = this.$refs.messageInput;
      elem.style.height = 'auto';
      elem.style.height = `${elem.scrollHeight}px`;
    },
    scrollToBottom(smooth = true) {
      this.$nextTick(() => {
        const elem = this.$refs.messageList;
        elem.scrollTo({top: elem.scrollHeight, behavior: smooth ? 'smooth' : 'instant'});
      });
    },
    sendMessage() {
      if (this.sending) return;
      if (this.v$.newMessage.text.$invalid) return;
      const text = this.newMessage.text.trim();
      const hasText = text.length > 0;
      const hasMedia = this.newMessage.media.length > 0;
      if (!hasText && !hasMedia) return;

      const authorDisplay = store.state.userInformation.firstName + ' ' + store.state.userInformation.lastName;

      const base = {
        type: 'sent',
        authorDisplay: authorDisplay,
        authorPictureUrl: null,
        platform: 'GAPP',
        sentMoment: moment.utc().toDate(),
        sending: true,
      };
      const pending = [];
      if (hasText) pending.push({...base, translatedContent: text});
      this.newMessage.media.forEach((m) => {
        pending.push({...base, translatedContent: '', mediaUrl: m.url, mediaType: m.type});
      });
      pending.forEach((m) => this.conversation.messages.push(m));
      this.scrollToBottom();

      const files = this.newMessage.media.map((m) => m.file);
      const mediaToRevoke = this.newMessage.media;
      this.newMessage = {text: '', media: []};
      this.sending = true;
      this.$nextTick(this.autoGrowMessageInput);

      threadService
        .sendMessage(Number(this.$route.params.threadId), hasText ? text : null, files)
        .then((createdMessages) => {
          pending.forEach((m) => {
            const index = this.conversation.messages.indexOf(m);
            if (index !== -1) this.conversation.messages.splice(index, 1);
          });
          createdMessages.forEach((created, i) => {
            this.conversation.messages.push({
              ...created,
              type: 'sent',
              authorPictureUrl: null,
              mediaUrl: pending[i]?.mediaUrl,
              mediaType: pending[i]?.mediaType,
            });
          });
          this.scrollToBottom();
          const lastMedia = mediaToRevoke.length ? mediaToRevoke[mediaToRevoke.length - 1] : null;
          this.$emit('message-sent', {
            authorDisplay: authorDisplay,
            message: hasMedia ? null : text,
            type: hasMedia ? lastMedia.type : 'text',
            moment: moment.utc().toDate(),
          });
        })
        .catch((e) => {
          pending.forEach((m) => {
            const index = this.conversation.messages.indexOf(m);
            if (index !== -1) this.conversation.messages.splice(index, 1);
          });
          this.addError(
            e.response?.status === 417
              ? 'Impossible d’envoyer le message.'
              : 'Une erreur est survenue lors de l’envoi du message.',
          );
        })
        .finally(() => {
          mediaToRevoke.forEach((m) => URL.revokeObjectURL(m.url));
          this.sending = false;
        });
    },
    resendMessage(messageId) {
      const message = this.conversation.messages.find((m) => m.id === messageId);
      if (!message || message.resending) return;
      message.resending = true;
      threadService
        .resendMessage(Number(this.$route.params.threadId), messageId)
        .then((updated) => {
          Object.assign(message, updated);
          // Le renvoi date le message à maintenant : il doit redevenir le plus récent de la liste.
          const index = this.conversation.messages.indexOf(message);
          if (index !== -1) this.conversation.messages.splice(index, 1);
          this.conversation.messages.push(message);
          this.scrollToBottom();
        })
        .catch(() => this.addError('Impossible de renvoyer le message.'))
        .finally(() => {
          message.resending = false;
        });
    },
    removeMedia(index) {
      URL.revokeObjectURL(this.newMessage.media[index].url);
      this.newMessage.media.splice(index, 1);
    },
    openDetailsSidebar() {
      this.$emit('openDetailsSidebar');
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.scrollToBottom(false);
        if (!this.$isMobile()) this.$refs.messageInput.focus();
      }
    },
    immediate: true,
    deep: true,
  },
};
</script>

<template>
  <div class="flex flex-col h-full divide-y divide-grey-100">
    <thread-messages-header
      v-if="!loading"
      :conversation="conversation"
      :thread-id="Number($route.params.threadId)"
      :archivable="archivable"
      @archive="$emit('archive')" />
    <div v-else class="w-full flex p-4 items-center gap-4 h-[80px]">
      <div class="flex shrink-0">
        <AkShimmer :loading="true" width="44px" height="44px" border-radius="9999px" />
      </div>
      <span class="grow">
        <AkShimmer :loading="true" width="110px" height="18px" />
      </span>
      <div class="flex gap-2 items-center shrink-0">
        <AkShimmer :loading="true" width="102px" height="40px" border-radius="8px" />
        <AkShimmer :loading="true" width="40px" height="40px" border-radius="9999px" />
      </div>
    </div>
    <div
      class="flex w-full relative flex-col flex-1 min-h-0 overflow-y-auto gap-12 md:px-8 px-4 pt-6"
      ref="messageList">
      <template v-if="!loading">
        <button
          @click="openDetailsSidebar"
          class="floating-action-bar bg-white/90 backdrop-blur-sm active:bg-grey-50/90 z-10 xl:hidden flex gap-3 sticky md:-top-2 -top-4 md:-mx-4 -mx-2 md:-mt-2 -mt-4 left-2 right-2 p-2 pr-3 border border-grey-100 hover:border-grey-200 rounded-xl items-center cursor-pointer">
          <AkPictureAccommodation
            :id="modelValue.accommodation.id"
            :md5="modelValue.accommodation.pictureMd5"
            :height="44"
            class="max-w-[44px]"
            disabled-link
            small
            radius="rounded-md"
            cover />
          <div class="flex text-left flex-col gap-0.5">
            <span class="text-sm text-grey-500">{{ statusLabel }}</span>
            <span class="text-grey-900 font-medium">
              <template v-if="isReservationConfirmed">Voir la réservation</template>
              <template v-else-if="isSpecialOfferVoided || isSpecialOfferExpired || isSpecialOfferActive">
                Voir l'offre
              </template>
              <template v-else>Voir la demande</template>
            </span>
          </div>
          <div class="min-h-8 min-w-8 bg-primary flex items-center justify-center rounded-full ml-auto">
            <i class="ga-icon ga-chevron_right text-white text-md" />
          </div>
        </button>
        <span class="self-center text-sm text-grey-500 font-medium text-center">
          Début de votre conversation avec {{ title }}
        </span>
        <div class="flex flex-col gap-1" v-for="group in messagesByDay" :key="group.date.toDateString()">
          <date-divider :label="dayLabel(group.date)" />
          <message-bubble
            v-for="message in group.messages"
            :key="moment.utc(message.sentMoment).toDate().getTime()"
            :message="message"
            :is-last-in-cluster="message.isLastInCluster"
            :is-first-in-cluster="message.isFirstInCluster"
            :is-last-message="message.id === lastMessage.id"
            :guests="conversation.guests"
            :last-messages-read="conversation.lastMessagesRead"
            @resend="resendMessage" />
        </div>
      </template>
      <template v-else>
        <div class="flex flex-col gap-1 mb-2">
          <div class="flex md:mr-[min(80px,15%)] pr-6 py-3 px-4">
            <AkShimmer :loading="true" width="220px" height="18px" />
          </div>
        </div>
        <div class="flex flex-col gap-1 mb-2">
          <div class="flex md:mr-[min(80px,15%)] pr-6 py-3 px-4">
            <AkShimmer :loading="true" width="150px" height="18px" />
          </div>
          <div class="flex items-center gap-2">
            <AkShimmer :loading="true" width="28px" height="28px" border-radius="9999px" />
            <AkShimmer :loading="true" width="140px" height="16px" />
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <div class="flex md:ml-[min(80px,15%)] self-end pl-6 py-3 px-4">
            <AkShimmer :loading="true" width="190px" height="18px" />
          </div>
          <div class="flex items-center gap-2 flex-row-reverse">
            <AkShimmer :loading="true" width="28px" height="28px" border-radius="9999px" />
            <AkShimmer :loading="true" width="140px" height="16px" />
          </div>
        </div>
      </template>
      <div class="grow"></div>
      <div
        class="sticky bottom-0 -mx-4 md:px-4 md:pb-4 px-2 pb-2 !border-t-0 bg-gradient-to-t from-white/90 to-white/0 backdrop-blur-sm">
        <Transition name="typing-fade">
          <button
            v-if="newMessageCount > 0"
            @click="jumpToBottom"
            class="absolute -top-12 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-white shadow-md border border-grey-100 rounded-full pl-3 pr-2 py-1.5 text-sm font-medium text-grey-900 hover:bg-grey-50">
            <span>
              {{ newMessageCount }} nouveau{{ newMessageCount > 1 ? 'x' : '' }} message{{
                newMessageCount > 1 ? 's' : ''
              }}
            </span>
            <i class="ga-icon ga-chevron_down text-md" />
          </button>
        </Transition>
        <div class="w-full rounded-lg border border-grey-100 shrink-0 overflow-hidden bg-white">
          <div
            class="flex flex-col md:p-4 p-2 pl-2.5 !pb-1 gap-2"
            :class="newMessage.media.length ? 'md:min-h-[180px]' : 'md:min-h-[120px]'">
            <div v-if="newMessage.media.length" class="flex gap-2 overflow-x-auto py-1 -my-1">
              <div v-for="(item, i) in newMessage.media" :key="i" class="relative h-16 w-16 shrink-0 bg-grey-50 group">
                <img
                  v-if="item.type === 'image'"
                  :src="item.url"
                  alt=""
                  class="h-full w-full object-cover rounded-lg" />
                <video v-else :src="item.url" class="h-full w-full object-cover" />
                <button
                  class="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-grey-900/70 opacity-0 group-hover:opacity-100 text-white flex items-center justify-center"
                  @click="removeMedia(i)">
                  <span class="ga-icon ga-supprimer text-[11px] font-bold" />
                </button>
              </div>
            </div>
            <div class="flex md:flex-col grow md:items-start items-center gap-2" id="mobileInputBar">
              <MessageMediaMenu v-if="$isMobile()" v-model:media="newMessage.media" class="order-1" />
              <textarea
                ref="messageInput"
                rows="1"
                placeholder="Écrire un message"
                class="grow shrink-0 md:w-full resize-none md:max-h-20 max-h-12 h-fit self-center md:py-0 py-3 order-1 overflow-auto"
                v-model="newMessage.text"
                @input="autoGrowMessageInput"
                @keydown.ctrl.enter.prevent="$refs.sendBtn.click()" />
              <button
                v-if="$isMobile()"
                :disabled="sending"
                class="font-medium !h-12 !w-12 focus-visible:bg-primary-700 btn-primary order-1"
                @click="sendMessage">
                <i class="ga-icon ga-send text-3xl"></i>
              </button>
            </div>
            <div
              class="hidden md:flex justify-end text-sm font-medium"
              :class="{
                'text-grey-500': newMessage.text.length < 1800,
                'text-orange-500': newMessage.text.length >= 1800 && newMessage.text.length <= 2000,
                'text-red-500': newMessage.text.length > 2000,
              }">
              {{ newMessage.text.length }}/2000
            </div>
          </div>

          <div class="hidden md:flex pr-2 pl-3 py-2.5 gap-2 items-center">
            <MessageMediaMenu
              v-model:media="newMessage.media"
              :thread-id="Number($route.params.threadId)"
              class="mr-auto"
              @select-template="onSelectTemplate" />
            <AkButtonActionV2
              ref="sendBtn"
              label="Répondre"
              :disabled="sending || !newMessage.text?.trim().length || v$.newMessage.text.$invalid"
              class="font-medium !h-12 !px-4 focus-visible:bg-primary-700 transition-background duration-100"
              @click="sendMessage" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.floating-action-bar {
  @apply shadow-sm;
  box-shadow:
    var(--tw-ring-offset-shadow, 0 0 #0000),
    var(--tw-ring-shadow, 0 0 #0000),
    var(--tw-shadow),
    inset 0 1px 3px #fff !important;
}
</style>
